import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {DeviceService} from '../../services/device.service';
import {Device, NeedSyncChangeData} from '@app/core/models/device';
import {interval, Subject, Subscription} from 'rxjs';
import {filter, skip, take, takeUntil} from 'rxjs/operators';
import {AuthService, UserRole} from '@app/core/auth/auth.service';
import {SidebarComponent} from '../sidebar/sidebar.component';
import {Feature} from '@app/core/constants/feature-constants';
import {Router} from '@angular/router';
import {ModalService} from '@app/core/services/modal.service';
import {MessageBoxIcon, ModalButton, ModalButtons} from '@app/core/models/modal-ref';
import {environment} from '../../../../environments/environment';
import {NGXLogger} from 'ngx-logger';
import {ScgSharedFactoryService} from '@app/core/services/scg-shared-factory.service';
import {getModalextensionConfig} from '@app/shared/modals/modalextension/modalextension.component';
import {ModaldevicenameComponent} from '@app/shared/modals/modaldevicename/modaldevicename.component';
import {ModalprofilesentryComponent} from '@app/shared/modals/modalprofilesentry/modalprofilesentry.component';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {

  public UserRole = UserRole;
  public environment = environment;
  public Feature = Feature;

  private onDestroy = new Subject<boolean>();
  private needSyncSubscription: Subscription;

  /** True if needSync was not changed since last device switch */
  private initialSyncChange = true;

  public activeDevice: Device;
  public activeUser: string;

  public licenceStatus = 'Unknown';

  public showNeedSync = false;
  public showNeedSyncDone = false;

  /**
   * Required to open/close the mobile sidebar on hamburger button click
   */
  @Input() public sidebar: SidebarComponent;

  constructor(
    public deviceService: DeviceService,
    public auth: AuthService,
    private modalService: ModalService,
    private modal: ModalService,
    private log: NGXLogger,
    private router: Router,
    private scgShared: ScgSharedFactoryService
  ) { }

  ngOnInit(): void {
    this.deviceService.onDeviceSwitch
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (activeDevice) => {
          // Each device switch resets initialSyncChange
          this.initialSyncChange = true;

          if (activeDevice.device !== this.activeDevice) {
            this.activeDevice = activeDevice.device;
          }

          this.activeUser = activeDevice.username;

          this.licenceStatus = 'Unknown';
          this.deviceService.activeDevice.onLincenceChange
            .pipe(takeUntil(this.onDestroy))
            .subscribe({
              next: (licence) => {
                if (!licence.isExpired()) {
                  this.licenceStatus = 'Licenced';
                } else {
                  this.licenceStatus = 'Expired';
                }
              }
            });

          // Sync notice
          this.needSyncSubscription?.unsubscribe();    // we need to unsubscribe the "old" device and subscribe it new afterwards
          this.needSyncSubscription = activeDevice.device.needSyncChange.subscribe((needSync) => {
            this.updateSyncNotice(needSync);
          });
        }
      });
  }

  ngOnDestroy() {
    this.onDestroy.next(true);
    this.onDestroy.complete();
  }

  public isReport(): boolean {
    return (this.auth.role === UserRole.Report);
  }

  public onLogoutClick() {
    this.auth.logout();
  }

  public showBackButton(): boolean {
    let url = this.router.url;

    if (url.includes('?')) {
      url = url.substr(0, url.indexOf('?'));
    }

    /*
     * There is a short period of time where the back button is shown on tokenlogin
     * (after the user is logged in but before the switch to readonly-report) is finished.
     * To prevent the back button from popping up during this time frame, we disallow tokenlogin/...
     */
    return (url !== '/readonly-report' && !url.startsWith('/tokenlogin'));
  }

  public isActiveRouteDeviceNotFound() {
    return this.router.url === '/devicenotfound';
  }

  private updateSyncNotice(needSync: NeedSyncChangeData) {
    this.showNeedSync = needSync.needSync;
    this.showNeedSyncDone = false;

    // Prevent that we show needSyncDone if the initial needSync value is false
    // (we only want to show the notice if we made a local settings change before which was synced successfully)
    if (!needSync.needSync && needSync.localCause && !this.initialSyncChange) {
      this.showNeedSyncDone = true;

      // Show needSyncDone for the timespan below
      interval(5000)
        .pipe(
          take(1),
          // Cancel interval if needSync changed to true in the meanwhile. This prevents the needSyncDone from closing too early
          // if it is shown for a second time while the first needSyncDone did not even disappear
          takeUntil(this.activeDevice.needSyncChange.pipe(filter(res => res.needSync === true))),
          // Cancel interval if device was switched (first device is skipped because it's the current active device)
          takeUntil(this.deviceService.onDeviceSwitch.pipe(skip(1)))
        )
        .subscribe(() => {
          this.showNeedSyncDone = false;
        });
    }

    this.initialSyncChange = false;
  }

  public showReadOnlyMsg() {
    console.log('showReadOnly');
    this.modal.showMessageBox({
      text: 'MESSAGE.READ_ONLY',
      title: 'MESSAGE.INFO',
      buttons: ModalButtons.Ok,
      closeOnBackdropClick: false,
      defaultButton: ModalButton.Ok,
      icon: MessageBoxIcon.Information
    });
  }

  public gaLog(action: string) {
    this.log.info('gaLog action=' + action);
    this.scgShared.gaEvent(action);
  }

  public showProfileModal() {
    this.modalService.show({
      content: ModalprofilesentryComponent,
      closeOnBackdropClick: false,
      title: 'COMMON.PLEASE_SELECT',
      buttons: ModalButtons.OkCancel
    });
  }



  // public onActiveDeviceChange() {
  //   if (this.activeDevice.users && this.activeDevice.users.length > 0) {
  //     this.activeUser = this.activeDevice.users[0];
  //     this.deviceService.switchDevice(this.activeDevice, this.activeUser);
  //   } else {
  //     this.deviceService.switchDevice(this.activeDevice);
  //   }
  // }

  // public onActiveUserChange() {
  //   this.deviceService.switchDevice(this.activeDevice, this.activeUser);
  // }
}
